(() => {
  if (window.ApellaFooterMotion) return;
  window.ApellaFooterMotion = true;
  const instances = new Map();
  const sewingSpeed = 1.3;
  const threadSpeed = 390 * sewingSpeed;
  const clamp = (v) => Math.max(0, Math.min(1, v));
  const ease = (v) => { const t = clamp(v); return t * t * (3 - 2 * t); };
  const mix = (a, b, t) => a + (b - a) * t;

  // Follow the natural strokes once. Curvature-weighted arc length slows the
  // hand at tight turns; the cotton treatment is clipped to the exact artwork.
  const measure = (path) => {
    const length = path.getTotalLength();
    const count = Math.ceil(length / 3);
    const samples = [];
    let weight = 0;
    for (let i = 0; i <= count; i++) {
      const distance = length * i / count;
      const p = path.getPointAtLength(distance);
      const a = path.getPointAtLength(Math.max(0, distance - 7));
      const b = path.getPointAtLength(Math.min(length, distance + 7));
      const incoming = Math.atan2(p.y - a.y, p.x - a.x);
      const outgoing = Math.atan2(b.y - p.y, b.x - p.x);
      const turn = Math.abs(Math.atan2(Math.sin(outgoing - incoming), Math.cos(outgoing - incoming)));
      if (i) weight += length / count * (1 + Math.min(turn, .85) * 1.8);
      samples.push({ distance, weight, x: p.x, y: p.y, angle: 132 + Math.sin(outgoing) * 10 });
    }
    return { path, length, samples, weight, duration: weight / threadSpeed };
  };
  const sample = (route, progress) => {
    const t = clamp(progress);
    const ramp = .075;
    const paced = t < ramp ? t * t / (2 * ramp * (1 - ramp))
      : t > 1 - ramp ? 1 - (1 - t) ** 2 / (2 * ramp * (1 - ramp))
      : (t - ramp / 2) / (1 - ramp);
    const target = paced * route.weight;
    let low = 0;
    let high = route.samples.length - 1;
    while (low < high) {
      const middle = (low + high) >> 1;
      if (route.samples[middle].weight < target) low = middle + 1;
      else high = middle;
    }
    const b = route.samples[low];
    const a = route.samples[Math.max(0, low - 1)];
    const fraction = (target - a.weight) / (b.weight - a.weight || 1);
    return { x: mix(a.x, b.x, fraction), y: mix(a.y, b.y, fraction), angle: mix(a.angle, b.angle, fraction), distance: mix(a.distance, b.distance, fraction) };
  };
  const mount = (root) => {
    root.querySelectorAll('[data-apella-footer]').forEach((footer) => {
      if (instances.has(footer)) return;
      const signature = footer.querySelector('.apella-footer__signature');
      const art = footer.querySelector('[data-footer-logo-art]');
      if (!art) return;
      const get = (name) => art.querySelector(`[data-${name}]`);
      const thread = get('logo-thread');
      const ink = get('logo-ink');
      const complete = get('logo-complete');
      const needle = get('logo-needle');
      const supply = get('logo-supply');
      const feed = get('logo-feed');
      const spool = get('spool-turn');
      const winding = get('spool-winding');
      if (![thread, ink, complete, needle, supply, feed, spool, winding].every(Boolean)) return;
      const fibers = [...winding.querySelectorAll('[data-spool-fiber]')];
      const feedX = Number(feed.dataset.feedX);
      const feedY = Number(feed.dataset.feedY);
      const strokes = [...art.querySelectorAll('[data-logo-stroke]')].map(measure);
      const timeline = [];
      let cursor = .65;
      strokes.forEach((stroke, index) => {
        const previous = strokes[index - 1];
        const start = stroke.samples[0];
        if (previous) {
          const end = previous.samples[previous.samples.length - 1];
          const gap = Math.hypot(start.x - end.x, start.y - end.y);
          if (gap > 12) {
            const duration = (.24 + gap / 720) / sewingSpeed;
            timeline.push({ type: 'travel', start: cursor, end: cursor + duration, from: end, to: start, index });
            cursor += duration;
          }
        }
        stroke.start = cursor;
        cursor += stroke.duration;
        stroke.end = cursor;
        timeline.push({ type: 'drawing', start: stroke.start, end: stroke.end, stroke, index });
        stroke.path.setAttribute('stroke-dasharray', `${stroke.length} ${stroke.length}`);
      });
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      const arriveEnd = .65;
      const drawEnd = cursor;
      const threadEnd = drawEnd + 1.1;
      const morphEnd = threadEnd + 1.5;
      const restEnd = morphEnd + 3.4;
      const cycleLength = restEnd + 1.2;
      let visible = false;
      let loaded = false;
      let destroyed = false;
      let frame = 0;
      let last = 0;
      let time = 0;
      let angle = strokes[0].samples[0].angle;
      let previousDistance = 0;
      let slack = 48;

      const paint = (cycle, delta) => {
        const segment = timeline.find((part) => cycle >= part.start && cycle < part.end);
        let point = sample(cycle < arriveEnd ? strokes[0] : strokes[strokes.length - 1], cycle < arriveEnd ? 0 : 1);
        let distance = 0;
        strokes.forEach((stroke) => {
          const progress = clamp((cycle - stroke.start) / stroke.duration);
          const drawn = sample(stroke, progress);
          distance += drawn.distance;
          stroke.path.setAttribute('stroke-dashoffset', String(stroke.length - drawn.distance));
          stroke.path.setAttribute('opacity', progress > 0 ? '1' : '0');
          if (segment?.stroke === stroke) point = drawn;
        });
        if (segment?.type === 'travel') {
          const t = ease((cycle - segment.start) / (segment.end - segment.start));
          point = { x: mix(segment.from.x, segment.to.x, t), y: mix(segment.from.y, segment.to.y, t) - Math.sin(t * Math.PI) * 18, angle: mix(segment.from.angle, segment.to.angle, t) + Math.sin(t * Math.PI) * 6 };
        }
        const morph = ease((cycle - threadEnd) / (morphEnd - threadEnd));
        const fade = 1 - ease((cycle - restEnd) / .95);
        const toolsOpacity = ease(cycle / .45) * (1 - ease((morph - .2) / .7));
        art.dataset.logoPhase = cycle < arriveEnd ? 'arrive' : cycle < drawEnd ? segment.type
          : cycle < threadEnd ? 'thread' : cycle < morphEnd ? 'morph' : cycle < restEnd ? 'hold' : 'reset';
        thread.setAttribute('opacity', String(1 - morph));
        // The stitched silhouette is the original alpha mask, never a substitute
        // handwritten font. Fill between the cotton fibers for the final mark.
        ink.setAttribute('opacity', String(morph * fade));
        complete.setAttribute('opacity', String(ease((cycle - drawEnd) / .2)));

        const arrival = 1 - ease(cycle / arriveEnd);
        const x = point.x - arrival * 18;
        const y = point.y - arrival * 15;
        // Keep the point touching the last stitch until the tools fade away.
        angle = mix(angle, point.angle, 1 - Math.exp(-delta * 10));
        needle.setAttribute('transform', `translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${angle.toFixed(2)})`);
        needle.setAttribute('opacity', String(toolsOpacity));
        supply.setAttribute('opacity', String(toolsOpacity));

        // The supply strand ends at the actual eye, after the needle transform.
        const radians = angle * Math.PI / 180;
        const eyeX = x - 125 * Math.cos(radians) - .8 * Math.sin(radians);
        const eyeY = y - 125 * Math.sin(radians) + .8 * Math.cos(radians);
        const paidOut = Math.max(0, distance - previousDistance);
        const pull = delta > 0 ? clamp(paidOut / delta / threadSpeed) : 0;
        slack = mix(slack, 20 + 32 * (1 - pull), 1 - Math.exp(-delta * 6));
        previousDistance = distance;
        const span = eyeX - feedX;
        feed.setAttribute('d', `M ${feedX} ${feedY} C ${feedX + span * .28} ${feedY + 32 + slack} ${eyeX - span * .18} ${eyeY + 80 + slack} ${eyeX} ${eyeY}`);
        // Rotation follows paid-out length, so both stop together at the finish.
        const rotation = distance * .34;
        spool.setAttribute('transform', `rotate(${rotation.toFixed(2)})`);
        // Winding ridges roll across the curved cotton surface and disappear
        // around its back, while the wooden end turns on the same axle.
        fibers.forEach((fiber, index) => {
          const phase = rotation * Math.PI / 180 + index * Math.PI * 2 / fibers.length;
          const y1 = Math.sin(phase) * 29;
          const y2 = Math.sin(phase + .28) * 31;
          const y3 = Math.sin(phase + .54) * 30;
          fiber.setAttribute('d', `M -30 ${y1} C -13 ${y1} 13 ${y2} 34 ${y3}`);
          fiber.setAttribute('opacity', String(Math.max(0, Math.cos(phase)) * .8));
        });
      };
      const render = (stamp) => {
        const delta = last ? (stamp - last) / 1000 : 0;
        last = stamp;
        time += delta;
        paint(time % cycleLength, delta);
        frame = requestAnimationFrame(render);
      };
      const sync = () => {
        cancelAnimationFrame(frame);
        frame = 0;
        last = 0;
        const running = loaded && visible && !reduced.matches && !document.hidden;
        footer.toggleAttribute('data-motion-running', running);
        signature.toggleAttribute('data-logo-ready', loaded && !reduced.matches);
        if (reduced.matches) { time = 0; previousDistance = 0; }
        if (running) { paint(time % cycleLength, 1 / 60); frame = requestAnimationFrame(render); }
      };
      const observer = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting && entry.intersectionRatio >= .25;
        sync();
      }, { threshold: .25 });
      observer.observe(signature);
      reduced.addEventListener('change', sync);
      document.addEventListener('visibilitychange', sync);
      const source = new Image();
      source.onload = () => { if (!destroyed) { loaded = true; paint(0, 1 / 60); sync(); } };
      source.src = ink.getAttribute('href');
      instances.set(footer, () => {
        destroyed = true;
        source.onload = null;
        cancelAnimationFrame(frame);
        observer.disconnect();
        reduced.removeEventListener('change', sync);
        document.removeEventListener('visibilitychange', sync);
        signature.removeAttribute('data-logo-ready');
      });
    });
  };
  mount(document);
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((cleanup, footer) => {
      if (event.target.contains(footer)) { cleanup(); instances.delete(footer); }
    });
  });
})();
